import * as ScoringActions from '../actions/scoring.actions';
import { DriverModel } from '../../../scoring/models/driver.model';
import { RankTypeEnum } from '../../../scoring/models/rank-type.enum';
import { ScoringRankModel } from '../../../scoring/models/scoring-rank.model';
import * as moment from 'moment-timezone';
import { PeriodStatsModel } from '../../../scoring/models/period-stats.model';
import { ScoringSummaryModel } from '../../../scoring/models/scoring-summary.model';
import uniqBy from 'lodash-es/uniqBy';


export interface State {
  dates: any[];
  driverList: DriverModel[] | null;
  driver: DriverModel | null;
  rankingData: Map<RankTypeEnum, ScoringRankModel[]>;
  vehiclePeriodStats: PeriodStatsModel | null;
  vehicleSummary: ScoringSummaryModel | null;
  driverPeriodStats: PeriodStatsModel | null;
  driverSummary: ScoringSummaryModel | null;
  selectedDriverPeriodStats: PeriodStatsModel | null;
  selectedDriverSummary: ScoringSummaryModel | null;
  groupPeriodStats: PeriodStatsModel | null;
  groupSummary: ScoringSummaryModel | null;
  error: any;
  isSummaryLoading: boolean;
  isPeriodsChartLoading: boolean;
  isGroupsRankingLoading: boolean;
  isVehiclesRankingLoading: boolean;
  isDriversRankingLoading: boolean;
}

export const initialState: State = {
  dates: [ moment().startOf('M').subtract(1, 'M').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD') ],
  driverList: null,
  driver: null,
  rankingData: new Map<RankTypeEnum, ScoringRankModel[]>(),
  vehiclePeriodStats: null,
  vehicleSummary: null,
  driverPeriodStats: null,
  driverSummary: null,
  selectedDriverPeriodStats: null,
  selectedDriverSummary: null,
  groupPeriodStats: null,
  groupSummary: null,
  error: null,
  isSummaryLoading: false,
  isPeriodsChartLoading: false,
  isGroupsRankingLoading: false,
  isVehiclesRankingLoading: false,
  isDriversRankingLoading: false
};

export function reducer(
  state = initialState,
  action: ScoringActions.ScoringActionsUnion
): State {

  switch (action.type) {
    case ScoringActions.changeDateRange.type:
      const dateFrom = moment(action.dateFrom).format('YYYY-MM-DD');
      const dateTo = moment(action.dateTo).format('YYYY-MM-DD');
      return {
        ...state,
        dates: [ dateFrom, dateTo ]
      };

    case ScoringActions.getDriverListSuccess.type:
      return {
        ...state,
        driverList: action.data
      };

    case ScoringActions.getDriverListFailure.type:
      return {
        ...state,
        error: action.error
      };

    case ScoringActions.getRankingData.type:
      return {
        ...state,
        isDriversRankingLoading: action.rank === RankTypeEnum.DRIVERS ? true : state.isDriversRankingLoading,
        isGroupsRankingLoading: action.rank === RankTypeEnum.GROUPS ? true : state.isGroupsRankingLoading,
        isVehiclesRankingLoading: action.rank === RankTypeEnum.VEHICLES ? true : state.isVehiclesRankingLoading,
      };

    case ScoringActions.getRankingDataSuccess.type:
      const newRankingData: Map<RankTypeEnum, ScoringRankModel[]> = new Map<RankTypeEnum, ScoringRankModel[]>(state.rankingData);
      newRankingData.set(action.rank, action.data);
      let driverList = state.driverList;
      if (action.rank === RankTypeEnum.DRIVERS) {
        driverList = uniqBy(action.data.map(item => ({ driverId: item.id, driverName: item.name, generalScoring: item.score })), 'driverId');
      }
      return {
        ...state,
        isDriversRankingLoading: action.rank === RankTypeEnum.DRIVERS ? false : state.isDriversRankingLoading,
        isGroupsRankingLoading: action.rank === RankTypeEnum.GROUPS ? false : state.isGroupsRankingLoading,
        isVehiclesRankingLoading: action.rank === RankTypeEnum.VEHICLES ? false : state.isVehiclesRankingLoading,
        rankingData: newRankingData,
        driverList: driverList
      };

    case ScoringActions.getRankingDataFailure.type:
      return {
        ...state,
        isDriversRankingLoading: false,
        isGroupsRankingLoading: false,
        isVehiclesRankingLoading: false
      };


    case  ScoringActions.getGroupsSummary.type:
      return {
        ...state,
        isSummaryLoading: true
      };

    case ScoringActions.getGroupsSummarySuccess.type:
      return {
        ...state,
        isSummaryLoading: false,
        groupSummary: action.data
      };

    case ScoringActions.getGroupsSummaryFailure.type:
      return {
        ...state,
        isSummaryLoading: false
      };


    case ScoringActions.getGroupsPeriodStats.type:
      return {
        ...state,
        isPeriodsChartLoading: true
      };

    case ScoringActions.getGroupsPeriodStatsSuccess.type:
      return {
        ...state,
        isPeriodsChartLoading: false,
        groupPeriodStats: action.data
      };


    case ScoringActions.getVehiclesPeriodStats.type:
      return {
        ...state,
        isPeriodsChartLoading: true
      };

    case ScoringActions.getVehiclesPeriodStatsSuccess.type:
      return {
        ...state,
        isPeriodsChartLoading: false,
        vehiclePeriodStats: action.data
      };

    case ScoringActions.getVehiclesSummarySuccess.type:
      return {
        ...state,
        vehicleSummary: action.data
      };

    case ScoringActions.setDriverSuccess.type:
      return {
        ...state,
        driver: action.data
      };


    case ScoringActions.getDriverPeriodStats.type:
      return {
        ...state,
        isPeriodsChartLoading: true
      };

    case ScoringActions.getDriverPeriodStatsSuccess.type:
      return {
        ...state,
        isPeriodsChartLoading: false,
        driverPeriodStats: action.data
      };

    case ScoringActions.getDriverSummarySuccess.type:
      return {
        ...state,
        driverSummary: action.data
      };

    case ScoringActions.getSelectedDriverPeriodStatsSuccess.type:
      return {
        ...state,
        selectedDriverPeriodStats: action.data
      };

    case ScoringActions.getSelectedDriverSummarySuccess.type:
      return {
        ...state,
        selectedDriverSummary: action.data
      };
    default: {
      return state;
    }
  }
}

export const getDates = (state: State) => state.dates;
export const getRankingData = (state: State) => state?.rankingData;
export const getVehicleSummary = (state: State) => state?.vehicleSummary;
export const getVehiclePeriodStats = (state: State) => state?.vehiclePeriodStats;
export const getRankingDataByRank = (state: State, rank: RankTypeEnum) => state?.rankingData?.get(rank);

export const getDriverList = (state: State) => state.driverList;
export const getDriverSummary = (state: State) => state.driverSummary;
export const getDriverPeriodStats = (state: State) => state.driverPeriodStats;
export const getSelectedDriverSummary = (state: State) => state.selectedDriverSummary;
export const getSelectedDriverPeriodStats = (state: State) => state.selectedDriverPeriodStats;
export const getDriver = (state: State) => state.driver;

export const getGroupSummary = (state: State) => state?.groupSummary;
export const getGroupPeriodStats = (state: State) => state?.groupPeriodStats;

export const hasError = (state: State) => state.error;

export const getIsSummaryLoading = (state: State) => state.isSummaryLoading;
export const getIsPeriodsChartLoading = (state: State) => state.isPeriodsChartLoading;
export const getIsDriversRankingLoading = (state: State) => state.isDriversRankingLoading;
export const getIsGroupsRankingLoading = (state: State) => state.isGroupsRankingLoading;
export const getIsVehiclesRankingLoading = (state: State) => state.isVehiclesRankingLoading;
